import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { APP_NAMESPACE } from '@/meta'

/**
 * Préférences d'accessibilité, et annonceur pour lecteurs d'écran.
 *
 * ─── Pourquoi des réglages APPLICATIFS alors que le système en a déjà ───
 *
 * Le système expose `prefers-reduced-motion` et `prefers-contrast`, et le CSS les
 * respecte déjà. Mais :
 *
 *   * beaucoup d'utilisateurs ne savent pas que ces réglages existent, ou ne
 *     peuvent pas les changer (poste d'entreprise verrouillé) ;
 *   * le besoin est parfois LOCAL : quelqu'un veut une police plus grande dans
 *     un tableau dense sans agrandir tout son système ;
 *   * l'agrandissement du navigateur (Ctrl +) grossit tout, y compris les
 *     marges : sur une application dense, on perd la moitié de l'écran. Une
 *     échelle typographique seule garde la mise en page.
 *
 * D'où trois réglages, chacun avec un mode « automatique » qui suit le système :
 * on n'écrase jamais un choix déjà exprimé au niveau du poste.
 *
 * ─── L'annonceur ───
 *
 * Dans une application à page unique, la navigation ne recharge rien : un
 * lecteur d'écran ne dit RIEN quand l'utilisateur change d'écran, et rien non
 * plus quand une action asynchrone réussit. L'utilisateur voyant voit un toast ;
 * l'autre n'a aucun retour. Une région « live » corrige cela, et c'est le seul
 * moyen : aucun attribut ARIA statique n'annonce un changement.
 */

export type PreferenceMouvement = 'auto' | 'reduce' | 'full'
export type PreferenceContraste = 'auto' | 'high'
/** En pourcentage de la taille de référence. */
export type EchelleTypo = 100 | 112 | 125 | 140

export type Urgence = 'polite' | 'assertive'

interface PreferencesA11y {
  mouvement: PreferenceMouvement
  contraste: PreferenceContraste
  echelle: EchelleTypo
}

const CLE_STOCKAGE = `${APP_NAMESPACE}.a11y`

const DEFAUTS: PreferencesA11y = {
  mouvement: 'auto',
  contraste: 'auto',
  echelle: 100,
}

const ECHELLES: readonly EchelleTypo[] = [100, 112, 125, 140]

function lireStockage(): PreferencesA11y {
  try {
    const brut = localStorage.getItem(CLE_STOCKAGE)
    if (brut === null) return { ...DEFAUTS }
    const lu = JSON.parse(brut) as Partial<PreferencesA11y>

    // Validation champ par champ : un stockage écrit par une version
    // précédente, ou modifié à la main, ne doit pas produire un état invalide
    // qu'aucun réglage de l'interface ne permettrait de corriger.
    return {
      mouvement:
        lu.mouvement === 'reduce' || lu.mouvement === 'full' ? lu.mouvement : 'auto',
      contraste: lu.contraste === 'high' ? 'high' : 'auto',
      echelle: ECHELLES.includes(lu.echelle as EchelleTypo)
        ? (lu.echelle as EchelleTypo)
        : 100,
    }
  } catch {
    return { ...DEFAUTS }
  }
}

/**
 * Interroge une requête média, en tolérant l'ABSENCE de `matchMedia`.
 *
 * jsdom ne l'implémente pas : sans cette précaution, la simple création du store
 * lève, et tout test de composant qui touche à l'accessibilité échoue avec une
 * erreur qui ne parle pas du composant testé. Le repli est « pas de préférence
 * exprimée », qui est aussi le bon comportement là où l'API n'existe pas.
 */
function mediaCorrespond(requete: string): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false
  }
  return window.matchMedia(requete).matches
}

/**
 * Écoute une requête média. Retourne `false` si l'écoute n'a pas pu être posée —
 * l'appelant n'a alors rien à défaire.
 */
function surChangementMedia(requete: string, rappel: () => void): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false
  }
  const liste = window.matchMedia(requete)
  // `addEventListener` n'existe pas sur les MediaQueryList de Safari < 14 :
  // `addListener`, déprécié, y reste le seul moyen.
  if (typeof liste.addEventListener === 'function') {
    liste.addEventListener('change', rappel)
  } else if (typeof liste.addListener === 'function') {
    liste.addListener(rappel)
  }
  return true
}

const REQUETE_MOUVEMENT = '(prefers-reduced-motion: reduce)'
const REQUETE_CONTRASTE = '(prefers-contrast: more)'

function systemeReduitMouvement(): boolean {
  return mediaCorrespond(REQUETE_MOUVEMENT)
}

function systemeContrasteEleve(): boolean {
  return mediaCorrespond(REQUETE_CONTRASTE)
}

/**
 * Pose les préférences sur `<html>`, où le CSS les lit.
 *
 * Des attributs de données plutôt que des classes : `data-motion="reduce"` se
 * lit dans l'inspecteur sans deviner, et n'entre en conflit avec aucune classe
 * utilitaire.
 */
function appliquer(prefs: PreferencesA11y): void {
  const racine = document.documentElement

  const mouvementReduit =
    prefs.mouvement === 'reduce' ||
    (prefs.mouvement === 'auto' && systemeReduitMouvement())
  racine.dataset.motion = mouvementReduit ? 'reduce' : 'full'

  const contrasteEleve =
    prefs.contraste === 'high' || (prefs.contraste === 'auto' && systemeContrasteEleve())
  if (contrasteEleve) racine.dataset.contrast = 'high'
  else delete racine.dataset.contrast

  // Multiplicateur consommé par les jetons `--text-*` (voir le complément du
  // starter dans assets/main.css). Une variable plutôt qu'un attribut : le CSS
  // fait le calcul, sans une règle par palier.
  racine.style.setProperty('--font-scale', String(prefs.echelle / 100))
}

/**
 * Applique les préférences AVANT le montage de Vue. Appelé depuis `main.ts`, au
 * même titre que le thème : une police qui grossit après la première peinture
 * fait sauter toute la mise en page sous les yeux de l'utilisateur.
 */
export function initA11yEager(): void {
  appliquer(lireStockage())
}

export const useA11yStore = defineStore('a11y', () => {
  const prefs = ref<PreferencesA11y>(lireStockage())

  /** Message courant de chaque région « live ». */
  const politesse = ref('')
  const urgence = ref('')

  const mouvementReduit = computed(
    () =>
      prefs.value.mouvement === 'reduce' ||
      (prefs.value.mouvement === 'auto' && systemeReduitMouvement()),
  )

  function persister(): void {
    appliquer(prefs.value)
    try {
      localStorage.setItem(CLE_STOCKAGE, JSON.stringify(prefs.value))
    } catch {
      // Appliqué pour cette session, simplement non mémorisé.
    }
  }

  function definirMouvement(valeur: PreferenceMouvement): void {
    prefs.value.mouvement = valeur
    persister()
  }

  function definirContraste(valeur: PreferenceContraste): void {
    prefs.value.contraste = valeur
    persister()
  }

  function definirEchelle(valeur: EchelleTypo): void {
    prefs.value.echelle = valeur
    persister()
  }

  function reinitialiser(): void {
    prefs.value = { ...DEFAUTS }
    persister()
  }

  /**
   * Annonce un message à un lecteur d'écran.
   *
   * `polite` attend une pause dans la lecture en cours — c'est le défaut, et ce
   * qu'il faut pour un résultat d'action. `assertive` INTERROMPT : à réserver à
   * ce qui empêche de continuer (erreur bloquante, session expirée). Tout
   * annoncer en `assertive` rend l'application inutilisable au lecteur d'écran,
   * qui se fait couper en permanence.
   *
   * Le message est vidé puis reposé au tick suivant : une région « live » ne
   * réannonce PAS un texte identique au précédent. Sans ce passage par la chaîne
   * vide, « Élément supprimé » deux fois de suite n'est lu qu'une fois.
   */
  function annoncer(message: string, niveau: Urgence = 'polite'): void {
    const cible = niveau === 'assertive' ? urgence : politesse
    cible.value = ''
    // `requestAnimationFrame` plutôt que `nextTick` : il faut que le DOM ait été
    // PEINT avec la valeur vide pour que le changement suivant compte comme une
    // mutation aux yeux du lecteur d'écran.
    requestAnimationFrame(() => {
      cible.value = message
    })
  }

  if (typeof window !== 'undefined') {
    // Suivi du système en direct, pour les deux réglages en mode automatique.
    surChangementMedia(REQUETE_MOUVEMENT, () => {
      if (prefs.value.mouvement === 'auto') appliquer(prefs.value)
    })
    surChangementMedia(REQUETE_CONTRASTE, () => {
      if (prefs.value.contraste === 'auto') appliquer(prefs.value)
    })

    // Synchronisation entre onglets, comme pour le thème.
    window.addEventListener('storage', (evenement) => {
      if (evenement.key !== CLE_STOCKAGE) return
      prefs.value = lireStockage()
      appliquer(prefs.value)
    })
  }

  return {
    prefs,
    politesse,
    urgence,
    mouvementReduit,
    echelles: ECHELLES,
    definirMouvement,
    definirContraste,
    definirEchelle,
    reinitialiser,
    annoncer,
  }
})
